import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSetRecoilState } from 'recoil';
import { useQueryClient, QueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  useQuestionControllerFindById,
  useQuestionControllerFindByKey,
  useQuestionControllerFindByTreeId,
  useQuestionControllerUpdate,
  getQuestionControllerFindByTreeIdQueryKey,
  getQuestionControllerFindByKeyQueryKey,
  getQuestionControllerFindByIdQueryKey,
} from '../../../../api/generated/questions/questions';
import {
  useTranslationControllerBulkUpsert,
  useTranslationControllerSearch,
  translationControllerSearch,
} from '../../../../api/generated/translation/translation';
import {
  TranslationResponseDto,
  UpdateQuestionDto,
  UpsertTranslationDto,
} from '../../../../api/generated/types';
import type { QuestionData } from '../../../../shared/components/survey-builder/V2ModalQuestionConfigure';
import {
  useFileControllerUploadFile,
  useFileControllerGetQuestionImages,
  useFileControllerLinkImageToQuestion,
  useFileControllerUnlinkImageFromQuestion,
  getFileControllerGetQuestionImagesQueryKey,
} from '../../../../api/generated/files/files';
import { PaDataRepoState } from '../../../../../report/use-data';
import { resolveQuestionLocaleValues } from './resolveQuestionLocaleValues';
import { collectNestedQuestionTranslationEntries } from '../../../../shared/utils/nested-question-translation';

function genQuestionThirdPartyLabelKey(questionKey: string): string {
  return `${questionKey}-third-party-label`;
}

function genQuestionThirdPartyDescInlineKey(questionKey: string): string {
  return `${questionKey}-third-party-desc-inline`;
}

function genQuestionThirdPartyDescKey(questionKey: string): string {
  return `${questionKey}-third-party-desc`;
}

function genQuestionThirdPartyExampleKey(questionKey: string): string {
  return `${questionKey}-third-party-example`;
}

function genOptionThirdPartyLabelKey(questionKey: string, optionKey: string): string {
  return `${questionKey}-${optionKey}-third-party-label`;
}

// EQ-VAS specific label keys
export function genEqVasTopLabelKey(questionKey: string): string {
  return `${questionKey}-top-label`;
}

export function genEqVasBottomLabelKey(questionKey: string): string {
  return `${questionKey}-bottom-label`;
}

export function genEqVasValueBoxLabelKey(questionKey: string): string {
  return `${questionKey}-value-box-label`;
}

// Scale label key (per-value)
export function genScaleLabelKey(questionKey: string, value: number): string {
  return `${questionKey}-label-${value}`;
}

// 값이 비어있는지 확인
function isTrimEmpty(value: string | null | undefined): boolean {
  return !value || value.trim() === '';
}

function normalizeLocale(locale?: string): string {
  return (locale || 'english').toLowerCase().trim();
}

function buildNestedQuestionLabelMap(params: {
  questionKey?: string;
  answerOptionsMap?: Record<string, unknown> | null;
}): Record<number, string> {
  const entries = collectNestedQuestionTranslationEntries({
    questionKey: params.questionKey,
    answerOptionsMap: params.answerOptionsMap as Record<
      string,
      { question?: string; questionIndex?: number; order?: number } | undefined
    >,
  });

  return entries.reduce<Record<number, string>>((acc, entry) => {
    acc[entry.questionIndex] = entry.question;
    return acc;
  }, {});
}

interface AnswerOptionMetaLike {
  useThirdPartyLabel?: boolean;
  thirdPartyLabel?: string;
}

// API 응답 타입 (generated 타입이 불완전하므로 직접 정의)
interface QuestionApiResponse {
  id: number;
  questionKey: string;
  type: string;
  required?: boolean;
  optional?: boolean;
  enabled?: boolean;
  desc?: string | null;
  descInline?: string | null;
  exampleKey?: string | null;
  answerKeys?: string[];
  showConditions?: unknown[] | null;
  optionConditions?: unknown[] | null;
  typeOptions?: Record<string, unknown> | null;
  minSelectedOptions?: number | null;
  maxSelectedOptions?: number | null;
  maxLength?: number | null;
  global?: boolean;
  systemKey?: string | null;
  reportLabel?: string | null;
  answerOptionsMap?: Record<string, unknown> | null;
  isThirdParty?: boolean;
  thirdPartyLabel?: string | null;
  thirdPartyDescInline?: string | null;
  thirdPartyDesc?: string | null;
  thirdPartyExample?: string | null;
}

interface UseQuestionConfigureModalProps {
  surveyId: string | number | undefined;
  selectedTreeId: number | null;
  locale?: string;
  selectedLocale?: string;
  onLocaleChange?: (locale: string) => void;
  queryClient?: QueryClient;
  onSaveClose?: () => void;
}

export interface UseQuestionConfigureModalReturn {
  // Modal state
  isOpen: boolean;
  questionKey: string | undefined;
  questionId: number | undefined;

  // Data
  questionData: QuestionData | null;
  isLoading: boolean;

  // Labels (translations) - base locale
  questionLabel: string;
  answerLabels: Record<string, string>;
  currentLocaleAnswerLabels: Record<string, string>;

  // Translated labels (current locale)
  translatedQuestionLabel: string;
  translatedAnswerLabels: Record<string, string>;
  translatedNestedQuestionLabels: Record<number, string>;

  // Translation lock (current locale)
  translationLockedMap: Record<string, boolean>;
  handleTranslationLockChange: (key: string, locked: boolean) => void;

  // Desc, DescInline, Example 번역 값 (base locale)
  descInlineValue: string;
  descValue: string;
  exampleValue: string;

  // Desc, DescInline, Example 번역 값 (current locale)
  translatedDescInlineValue: string;
  translatedDescValue: string;
  translatedExampleValue: string;
  translatedThirdPartyLabel: string;
  translatedThirdPartyDescInline: string;
  translatedThirdPartyDesc: string;
  translatedThirdPartyExample: string;
  translatedOptionThirdPartyLabels: Record<string, string>;

  // Current locale
  currentLocale: string;

  // Handlers
  handleOpen: (questionKey: string, questionId: number) => void;
  handleClose: () => void;
  handleLabelChange: (value: string) => void;
  handleAnswerLabelChange: (key: string, label: string) => void;
  handleTranslatedLabelChange: (value: string) => void;
  handleTranslatedAnswerLabelChange: (key: string, label: string) => void;
  handleTranslatedNestedQuestionLabelChange: (questionIndex: number, label: string) => void;
  handleLocaleChange: (locale: string) => void;
  handleSave: (data: Partial<QuestionData>) => void;
  handleSaveAndStay: (data: Partial<QuestionData>) => void;

  // Desc, DescInline, Example 핸들러
  handleDescInlineChange: (value: string) => void;
  handleDescChange: (value: string) => void;
  handleExampleChange: (value: string) => void;
  handleTranslatedDescInlineChange: (value: string) => void;
  handleTranslatedDescChange: (value: string) => void;
  handleTranslatedExampleChange: (value: string) => void;
  handleTranslatedThirdPartyLabelChange: (value: string) => void;
  handleTranslatedThirdPartyDescInlineChange: (value: string) => void;
  handleTranslatedThirdPartyDescChange: (value: string) => void;
  handleTranslatedThirdPartyExampleChange: (value: string) => void;
  handleTranslatedOptionThirdPartyLabelChange: (optionKey: string, value: string) => void;

  // EQ-VAS 라벨 (base locale)
  eqVasTopLabelValue: string;
  eqVasBottomLabelValue: string;
  eqVasValueBoxLabelValue: string;

  // EQ-VAS 라벨 (current locale)
  translatedEqVasTopLabelValue: string;
  translatedEqVasBottomLabelValue: string;
  translatedEqVasValueBoxLabelValue: string;

  // EQ-VAS 라벨 핸들러
  handleEqVasTopLabelChange: (value: string) => void;
  handleEqVasBottomLabelChange: (value: string) => void;
  handleEqVasValueBoxLabelChange: (value: string) => void;
  handleTranslatedEqVasTopLabelChange: (value: string) => void;
  handleTranslatedEqVasBottomLabelChange: (value: string) => void;
  handleTranslatedEqVasValueBoxLabelChange: (value: string) => void;

  // Scale labels
  scaleLabels: Array<{ value: number; baseLabel: string; translatedLabel: string }>;
  showScaleLabels: boolean;
  setShowScaleLabels: React.Dispatch<React.SetStateAction<boolean>>;

  // Scale label 핸들러
  handleAddScaleLabel: () => void;
  handleRemoveScaleLabel: (value: number) => void;
  handleScaleLabelValueChange: (oldValue: number, newValue: number) => void;
  handleScaleLabelTextChange: (value: number, text: string) => void;
  handleScaleLabelTranslatedTextChange: (value: number, text: string) => void;

  // Image upload
  imageUrl: string | null;
  isUploading: boolean;
  handleImageUpload: (file: File) => void;
  handleImageRemove: () => void;

  // States
  isSaving: boolean;
}

export function useQuestionConfigureModal({
  surveyId,
  selectedTreeId,
  locale = 'english',
  selectedLocale,
  onLocaleChange,
  queryClient: externalQueryClient,
  onSaveClose,
}: UseQuestionConfigureModalProps): UseQuestionConfigureModalReturn {
  const internalQueryClient = useQueryClient();
  const queryClient = externalQueryClient || internalQueryClient;
  const baseLocale = normalizeLocale(locale);
  const resolvedSelectedLocale = normalizeLocale(selectedLocale || baseLocale);

  // Recoil state for live testing preview update
  const setPaDataState = useSetRecoilState(PaDataRepoState);

  // Modal state
  const [isOpen, setIsOpen] = useState(false);
  const [questionKey, setQuestionKey] = useState<string | undefined>();
  const [questionId, setQuestionId] = useState<number | undefined>();

  // Current locale state (selected locale in modal)
  const [currentLocale, setCurrentLocale] = useState(resolvedSelectedLocale);

  // Sync currentLocale with selected locale from parent page state
  useEffect(() => {
    setCurrentLocale(resolvedSelectedLocale);
  }, [resolvedSelectedLocale]);

  // Local label state (for base locale)
  const [questionLabel, setQuestionLabel] = useState('');
  const [answerLabels, setAnswerLabels] = useState<Record<string, string>>({});

  // Translated label state (for current locale when it differs from base locale)
  const [translatedQuestionLabel, setTranslatedQuestionLabel] = useState('');
  const [translatedAnswerLabels, setTranslatedAnswerLabels] = useState<Record<string, string>>({});
  const [translatedNestedQuestionLabels, setTranslatedNestedQuestionLabels] = useState<
    Record<number, string>
  >({});

  // Translation lock state (current locale)
  const [translationLockedMap, setTranslationLockedMap] = useState<Record<string, boolean>>({});
  const originalTranslationLockedMapRef = useRef<Record<string, boolean>>({});
  const translationMapRef = useRef<Record<string, TranslationResponseDto>>({});
  const translationSearchSignatureRef = useRef<string>('');

  // Desc, DescInline, Example 번역 값 (base locale)
  const [descInlineValue, setDescInlineValue] = useState('');
  const [descValue, setDescValue] = useState('');
  const [exampleValue, setExampleValue] = useState('');

  // Desc, DescInline, Example 번역 값 (current locale)
  const [translatedDescInlineValue, setTranslatedDescInlineValue] = useState('');
  const [translatedDescValue, setTranslatedDescValue] = useState('');
  const [translatedExampleValue, setTranslatedExampleValue] = useState('');
  const [translatedThirdPartyLabel, setTranslatedThirdPartyLabel] = useState('');
  const [translatedThirdPartyDescInline, setTranslatedThirdPartyDescInline] = useState('');
  const [translatedThirdPartyDesc, setTranslatedThirdPartyDesc] = useState('');
  const [translatedThirdPartyExample, setTranslatedThirdPartyExample] = useState('');
  const [translatedOptionThirdPartyLabels, setTranslatedOptionThirdPartyLabels] = useState<
    Record<string, string>
  >({});

  // EQ-VAS label state (base locale)
  const [eqVasTopLabelValue, setEqVasTopLabelValue] = useState('');
  const [eqVasBottomLabelValue, setEqVasBottomLabelValue] = useState('');
  const [eqVasValueBoxLabelValue, setEqVasValueBoxLabelValue] = useState('');

  // EQ-VAS label state (current locale)
  const [translatedEqVasTopLabelValue, setTranslatedEqVasTopLabelValue] = useState('');
  const [translatedEqVasBottomLabelValue, setTranslatedEqVasBottomLabelValue] = useState('');
  const [translatedEqVasValueBoxLabelValue, setTranslatedEqVasValueBoxLabelValue] = useState('');

  // Scale labels state
  const [scaleLabels, setScaleLabels] = useState<Array<{ value: number; baseLabel: string; translatedLabel: string }>>([]);
  const [showScaleLabels, setShowScaleLabels] = useState(false);

  // Image upload state
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch question images
  const { data: questionImagesData } = useFileControllerGetQuestionImages(questionId ?? 0, {
    query: {
      enabled: isOpen && !!questionId,
    },
  });

  // Update imageUrl when question images data changes
  useEffect(() => {
    if (questionImagesData) {
      const { images } = questionImagesData;
      if (images && images.length > 0) {
        setImageUrl(images[0].url);
      } else {
        setImageUrl(null);
      }
    }
  }, [questionImagesData]);

  // Fetch question data (for editing)
  const {
    data: rawQuestionResponse,
    isLoading: isLoadingQuestion,
    refetch,
  } = useQuestionControllerFindById(questionId ?? 0, {
    query: {
      enabled: isOpen && !!questionId,
    },
  });

  // Fetch question with labels (for base locale)
  const { data: questionWithLabels, isLoading: isLoadingLabels } = useQuestionControllerFindByKey(
    {
      surveyId: Number(surveyId) || 0,
      questionKey: questionKey || '',
      locale: baseLocale,
    },
    {
      query: {
        enabled: isOpen && !!surveyId && !!questionKey,
      },
    },
  );

  // Fetch translated labels (for current locale when it differs from base locale)
  const { data: translatedLabelsData, isLoading: isLoadingTranslated } =
    useQuestionControllerFindByKey(
      {
        surveyId: Number(surveyId) || 0,
        questionKey: questionKey || '',
        locale: currentLocale,
      },
      {
        query: {
          enabled: isOpen && !!surveyId && !!questionKey && currentLocale !== baseLocale,
        },
      },
    );

  const { data: questionsForCurrentLocale } = useQuestionControllerFindByTreeId(
    selectedTreeId ?? 0,
    { locale: currentLocale },
    {
      query: {
        enabled: isOpen && !!selectedTreeId && !!questionId,
      },
    },
  );

  // Cast API response to proper type (먼저 정의해야 descInline 등을 사용할 수 있음)
  const questionResponse = rawQuestionResponse as QuestionApiResponse | undefined;

  // isLoading 상태
  const isLoading =
    isLoadingQuestion || isLoadingLabels || (currentLocale !== baseLocale && isLoadingTranslated);

  // Convert API response to QuestionData
  const questionData = useMemo((): QuestionData | null => {
    if (!questionResponse) return null;

    return {
      id: questionResponse.id,
      questionKey: questionResponse.questionKey,
      type: questionResponse.type,
      required: questionResponse.required ?? questionResponse.optional === false,
      enabled: questionResponse.enabled ?? true,
      desc: questionResponse.desc ?? null,
      descInline: questionResponse.descInline ?? null,
      exampleKey: questionResponse.exampleKey ?? null,
      answerKeys: questionResponse.answerKeys ?? [],
      showConditions: questionResponse.showConditions ?? null,
      optionConditions: (questionResponse.optionConditions as QuestionData['optionConditions']) ?? null,
      typeOptions: (questionResponse.typeOptions as Record<string, unknown> | null) ?? null,
      minSelectedOptions: questionResponse.minSelectedOptions ?? null,
      maxSelectedOptions: questionResponse.maxSelectedOptions ?? null,
      maxLength: questionResponse.maxLength ?? null,
      global: questionResponse.global ?? false,
      systemKey: questionResponse.systemKey ?? null,
      reportLabel: questionResponse.reportLabel ?? null,
      answerOptionsMap:
        (questionResponse.answerOptionsMap as Record<string, unknown> | null) ?? null,
      isThirdParty: questionResponse.isThirdParty ?? false,
      thirdPartyLabel: questionResponse.thirdPartyLabel ?? null,
      thirdPartyDescInline: questionResponse.thirdPartyDescInline ?? null,
      thirdPartyDesc: questionResponse.thirdPartyDesc ?? null,
      thirdPartyExample: questionResponse.thirdPartyExample ?? null,
    };
  }, [questionResponse]);

  const localizedQuestionForCurrentLocale = useMemo(() => {
    if (!Array.isArray(questionsForCurrentLocale)) return undefined;
    return questionsForCurrentLocale.find((question) => {
      if (questionId && question.id === questionId) return true;
      if (questionKey && question.questionKey === questionKey) return true;
      return false;
    });
  }, [questionsForCurrentLocale, questionId, questionKey]);

  const currentLocaleAnswerLabels = useMemo(() => {
    if (!localizedQuestionForCurrentLocale) return {};

    const labels: Record<string, string> = {};
    (localizedQuestionForCurrentLocale.answerKeys || []).forEach((key) => {
      const optionData = localizedQuestionForCurrentLocale.answerOptionsMap?.[key] as
        | Record<string, unknown>
        | undefined;
      labels[key] = (optionData?.label as string | undefined) || '';
    });

    return labels;
  }, [localizedQuestionForCurrentLocale]);

  // Update local label when question with labels data changes (base locale)
  useEffect(() => {
    if (questionWithLabels) {
      const labels: Record<string, string> = {};
      const { answerKeys } = questionWithLabels;
      const apiAnswerLabels = questionWithLabels.answerLabels as Record<string, string> | undefined;

      if (answerKeys && apiAnswerLabels) {
        answerKeys.forEach((key: string) => {
          labels[key] = apiAnswerLabels[key] || '';
        });
      } else if (answerKeys) {
        // Fallback: no label
        answerKeys.forEach((key: string) => {
          labels[key] = '';
        });
      }

      // V1과 동일: descInline, desc, example 번역 값 초기화 (base locale)
      // API에서 descriptionInline, description, example 필드로 반환
      const apiData = questionWithLabels as {
        descriptionInline?: string;
        description?: string;
        example?: string;
      };
      const resolved = resolveQuestionLocaleValues({
        questionKey,
        answerKeys: answerKeys || [],
        descInlineKey:
          questionResponse?.descInline || (questionKey ? `${questionKey}-desc-inline` : undefined),
        descKey: questionResponse?.desc || (questionKey ? `${questionKey}-desc` : undefined),
        exampleKey:
          questionResponse?.exampleKey ||
          (questionKey ? `${questionKey}-example-label-key` : undefined),
        fallbackQuestionLabel: questionWithLabels.label || questionWithLabels.questionKey || '',
        fallbackAnswerLabels: labels,
        fallbackDescInlineValue: apiData.descriptionInline || '',
        fallbackDescValue: apiData.description || '',
        fallbackExampleValue: apiData.example || '',
      });
      setQuestionLabel(resolved.questionLabel);
      setAnswerLabels(resolved.answerLabels);
      setDescInlineValue(resolved.descInlineValue);
      setDescValue(resolved.descValue);
      setExampleValue(resolved.exampleValue);
    }
  }, [
    questionWithLabels,
    questionKey,
    questionResponse?.descInline,
    questionResponse?.desc,
    questionResponse?.exampleKey,
  ]);

  // Update translated labels when translated data changes (current locale)
  useEffect(() => {
    if (translatedLabelsData && currentLocale !== baseLocale) {
      const labels: Record<string, string> = {};
      const { answerKeys } = translatedLabelsData;
      const apiAnswerLabels = translatedLabelsData.answerLabels as
        | Record<string, string>
        | undefined;

      if (answerKeys && apiAnswerLabels) {
        answerKeys.forEach((key: string) => {
          labels[key] = apiAnswerLabels[key] || '';
        });
      }

      // V1과 동일: descInline, desc, example 번역 값 초기화 (current locale)
      // API에서 descriptionInline, description, example 필드로 반환
      const apiData = translatedLabelsData as {
        descriptionInline?: string;
        description?: string;
        example?: string;
      };
      const resolved = resolveQuestionLocaleValues({
        questionKey,
        answerKeys: answerKeys || [],
        descInlineKey:
          questionResponse?.descInline || (questionKey ? `${questionKey}-desc-inline` : undefined),
        descKey: questionResponse?.desc || (questionKey ? `${questionKey}-desc` : undefined),
        exampleKey:
          questionResponse?.exampleKey ||
          (questionKey ? `${questionKey}-example-label-key` : undefined),
        fallbackQuestionLabel: translatedLabelsData.label || '',
        fallbackAnswerLabels: labels,
        fallbackDescInlineValue: apiData.descriptionInline || '',
        fallbackDescValue: apiData.description || '',
        fallbackExampleValue: apiData.example || '',
      });
      setTranslatedQuestionLabel(resolved.questionLabel);
      setTranslatedAnswerLabels(resolved.answerLabels);
      setTranslatedDescInlineValue(resolved.descInlineValue);
      setTranslatedDescValue(resolved.descValue);
      setTranslatedExampleValue(resolved.exampleValue);
      setTranslatedNestedQuestionLabels(
        buildNestedQuestionLabelMap({
          questionKey,
          answerOptionsMap: (translatedLabelsData as { answerOptionsMap?: Record<string, unknown> })
            .answerOptionsMap,
        }),
      );
    } else if (currentLocale === baseLocale) {
      // Clear translated labels when switching back to base locale
      setTranslatedQuestionLabel('');
      setTranslatedAnswerLabels({});
      setTranslatedNestedQuestionLabels({});
      setTranslatedDescInlineValue('');
      setTranslatedDescValue('');
      setTranslatedExampleValue('');
      setTranslatedThirdPartyLabel('');
      setTranslatedThirdPartyDescInline('');
      setTranslatedThirdPartyDesc('');
      setTranslatedThirdPartyExample('');
      setTranslatedOptionThirdPartyLabels({});
    }
  }, [
    translatedLabelsData,
    currentLocale,
    baseLocale,
    questionKey,
    questionResponse?.descInline,
    questionResponse?.desc,
    questionResponse?.exampleKey,
  ]);

  // Guard: translated locale query can fall back to base locale labels.
  // Rehydrate translated labels from translation table for the selected locale.
  useEffect(() => {
    let canceled = false;
    if (
      !isOpen ||
      !surveyId ||
      !questionKey ||
      !translatedLabelsData ||
      currentLocale === baseLocale
    ) {
      return () => {
        canceled = true;
      };
    }

    const answerKeysForLocale = translatedLabelsData.answerKeys || [];
    const fallbackAnswerLabels: Record<string, string> = {};
    const apiAnswerLabels = translatedLabelsData.answerLabels as Record<string, string> | undefined;
    answerKeysForLocale.forEach((key) => {
      fallbackAnswerLabels[key] = apiAnswerLabels?.[key] || '';
    });

    const apiData = translatedLabelsData as {
      label?: string;
      descriptionInline?: string;
      description?: string;
      example?: string;
    };

    const descInlineKey = questionResponse?.descInline || `${questionKey}-desc-inline`;
    const descKey = questionResponse?.desc || `${questionKey}-desc`;
    const exampleKey = questionResponse?.exampleKey || `${questionKey}-example-label-key`;
    const nestedQuestionEntries = collectNestedQuestionTranslationEntries({
      questionKey,
      answerOptionsMap: ((translatedLabelsData as { answerOptionsMap?: Record<string, unknown> })
        .answerOptionsMap ||
        questionResponse?.answerOptionsMap ||
        null) as Record<
        string,
        { question?: string; questionIndex?: number; order?: number } | undefined
      > | null,
    });
    // EQ-VAS specific keys
    const eqVasTopKey = genEqVasTopLabelKey(questionKey);
    const eqVasBottomKey = genEqVasBottomLabelKey(questionKey);
    const eqVasValueBoxKey = genEqVasValueBoxLabelKey(questionKey);
    // Scale label keys
    const scaleLabelValsForLocale: number[] = questionData?.typeOptions?.scaleLabels || [];
    const scaleLabelKeysForLocale = scaleLabelValsForLocale.map((val: number) => genScaleLabelKey(questionKey, val));
    scaleLabels.forEach((entry) => {
      const k = genScaleLabelKey(questionKey, entry.value);
      if (!scaleLabelKeysForLocale.includes(k)) scaleLabelKeysForLocale.push(k);
    });
    const keys = Array.from(
      new Set([
        questionKey,
        ...answerKeysForLocale,
        descInlineKey,
        descKey,
        exampleKey,
        ...nestedQuestionEntries.map((entry) => entry.key),
        eqVasTopKey,
        eqVasBottomKey,
        eqVasValueBoxKey,
        ...scaleLabelKeysForLocale,
      ]),
    );

    translationControllerSearch({
      surveyId: Number(surveyId),
      locale: currentLocale,
      type: 'patient',
      keys,
    })
      .then((result) => {
        if (canceled) return;
        const entries = result?.translations || [];
        const translationValues: Record<string, string> = {};
        entries.forEach((entry) => {
          if (!entry.key) return;
          translationValues[entry.key] = entry.value || '';
        });
        const resolved = resolveQuestionLocaleValues({
          questionKey,
          answerKeys: answerKeysForLocale,
          descInlineKey,
          descKey,
          exampleKey,
          fallbackQuestionLabel: translatedLabelsData.label || '',
          fallbackAnswerLabels,
          fallbackDescInlineValue: apiData.descriptionInline || '',
          fallbackDescValue: apiData.description || '',
          fallbackExampleValue: apiData.example || '',
          translationValues,
        });
        setTranslatedQuestionLabel(resolved.questionLabel);
        setTranslatedAnswerLabels(resolved.answerLabels);
        setTranslatedDescInlineValue(resolved.descInlineValue);
        setTranslatedDescValue(resolved.descValue);
        setTranslatedExampleValue(resolved.exampleValue);
        // EQ-VAS translated labels
        setTranslatedEqVasTopLabelValue(translationValues[eqVasTopKey] || '');
        setTranslatedEqVasBottomLabelValue(translationValues[eqVasBottomKey] || '');
        setTranslatedEqVasValueBoxLabelValue(translationValues[eqVasValueBoxKey] || '');
        // Scale label translated values
        setScaleLabels((prev) =>
          prev.map((entry) => {
            const key = genScaleLabelKey(questionKey, entry.value);
            return { ...entry, translatedLabel: translationValues[key] || '' };
          })
        );
        originalLabelsRef.current.scaleLabels = originalLabelsRef.current.scaleLabels.map((entry) => {
          const key = genScaleLabelKey(questionKey, entry.value);
          return { ...entry, translatedLabel: translationValues[key] || '' };
        });
        const fallbackNestedQuestionLabels = buildNestedQuestionLabelMap({
          questionKey,
          answerOptionsMap: (translatedLabelsData as { answerOptionsMap?: Record<string, unknown> })
            .answerOptionsMap,
        });
        const resolvedNestedQuestionLabels = nestedQuestionEntries.reduce<Record<number, string>>(
          (acc, entry) => {
            acc[entry.questionIndex] =
              translationValues[entry.key] ||
              fallbackNestedQuestionLabels[entry.questionIndex] ||
              '';
            return acc;
          },
          {},
        );
        setTranslatedNestedQuestionLabels(resolvedNestedQuestionLabels);
      })
      .catch(() => {
        // no-op: keep values from translatedLabelsData
      });

    return () => {
      canceled = true;
    };
  }, [
    isOpen,
    surveyId,
    questionKey,
    currentLocale,
    baseLocale,
    translatedLabelsData,
    questionResponse?.descInline,
    questionResponse?.desc,
    questionResponse?.exampleKey,
  ]);

  // Guard: when /questions/by-key falls back to English, rehydrate base-locale labels from translation table.
  useEffect(() => {
    let canceled = false;
    if (!isOpen || !surveyId || !questionKey || !questionWithLabels) {
      return () => {
        canceled = true;
      };
    }

    const answerKeysForBase = questionWithLabels.answerKeys || [];
    const fallbackAnswerLabels: Record<string, string> = {};
    const apiAnswerLabels = questionWithLabels.answerLabels as Record<string, string> | undefined;
    answerKeysForBase.forEach((key) => {
      fallbackAnswerLabels[key] = apiAnswerLabels?.[key] || '';
    });

    const apiData = questionWithLabels as {
      descriptionInline?: string;
      description?: string;
      example?: string;
    };

    const descInlineKey = questionResponse?.descInline || `${questionKey}-desc-inline`;
    const descKey = questionResponse?.desc || `${questionKey}-desc`;
    const exampleKey = questionResponse?.exampleKey || `${questionKey}-example-label-key`;
    // EQ-VAS specific keys
    const eqVasTopKeyBase = genEqVasTopLabelKey(questionKey);
    const eqVasBottomKeyBase = genEqVasBottomLabelKey(questionKey);
    const eqVasValueBoxKeyBase = genEqVasValueBoxLabelKey(questionKey);
    // Scale label keys (base locale)
    const scaleLabelValsForBase: number[] = questionData?.typeOptions?.scaleLabels || [];
    const scaleLabelKeysForBase = scaleLabelValsForBase.map((val: number) => genScaleLabelKey(questionKey, val));
    const keys = Array.from(
      new Set([questionKey, ...answerKeysForBase, descInlineKey, descKey, exampleKey, eqVasTopKeyBase, eqVasBottomKeyBase, eqVasValueBoxKeyBase, ...scaleLabelKeysForBase]),
    );

    translationControllerSearch({
      surveyId: Number(surveyId),
      locale: baseLocale,
      type: 'patient',
      keys,
    })
      .then((result) => {
        if (canceled) return;
        const entries = result?.translations || [];
        const translationValues: Record<string, string> = {};
        entries.forEach((entry) => {
          if (!entry.key) return;
          translationValues[entry.key] = entry.value || '';
        });
        const resolved = resolveQuestionLocaleValues({
          questionKey,
          answerKeys: answerKeysForBase,
          descInlineKey,
          descKey,
          exampleKey,
          fallbackQuestionLabel: questionWithLabels.label || questionWithLabels.questionKey || '',
          fallbackAnswerLabels,
          fallbackDescInlineValue: apiData.descriptionInline || '',
          fallbackDescValue: apiData.description || '',
          fallbackExampleValue: apiData.example || '',
          translationValues,
        });
        setQuestionLabel(resolved.questionLabel);
        setAnswerLabels(resolved.answerLabels);
        setDescInlineValue(resolved.descInlineValue);
        setDescValue(resolved.descValue);
        setExampleValue(resolved.exampleValue);
        // EQ-VAS base locale labels
        setEqVasTopLabelValue(translationValues[eqVasTopKeyBase] || '');
        setEqVasBottomLabelValue(translationValues[eqVasBottomKeyBase] || '');
        setEqVasValueBoxLabelValue(translationValues[eqVasValueBoxKeyBase] || '');
        // Scale labels (base locale) — filter out values outside current range
        const rangeMin = Number(questionData?.typeOptions?.min ?? 1);
        const rangeMax = Number(questionData?.typeOptions?.max ?? 10);
        const loadedScaleLabels = scaleLabelValsForBase
          .filter((val: number) => val >= rangeMin && val <= rangeMax)
          .map((val: number) => {
            const key = genScaleLabelKey(questionKey, val);
            return { value: val, baseLabel: translationValues[key] || '', translatedLabel: '' };
          });
        setScaleLabels(loadedScaleLabels);
        setShowScaleLabels(loadedScaleLabels.length > 0);
        originalLabelsRef.current.scaleLabels = loadedScaleLabels.map((l) => ({ ...l }));
      })
      .catch(() => {
        // no-op: keep fallback values from questionWithLabels
      });

    return () => {
      canceled = true;
    };
  }, [
    isOpen,
    surveyId,
    questionKey,
    baseLocale,
    questionWithLabels,
    questionResponse?.descInline,
    questionResponse?.desc,
    questionResponse?.exampleKey,
  ]);

  // Original labels ref (to track changes for translation save)
  const originalLabelsRef = useRef<{
    questionLabel: string;
    answerLabels: Record<string, string>;
    nestedQuestionLabels: Record<number, string>;
    translatedQuestionLabel: string;
    translatedAnswerLabels: Record<string, string>;
    translatedNestedQuestionLabels: Record<number, string>;
    // desc, descInline, example 원본 값
    descInlineValue: string;
    descValue: string;
    exampleValue: string;
    translatedDescInlineValue: string;
    translatedDescValue: string;
    translatedExampleValue: string;
    translatedThirdPartyLabel: string;
    translatedThirdPartyDescInline: string;
    translatedThirdPartyDesc: string;
    translatedThirdPartyExample: string;
    translatedOptionThirdPartyLabels: Record<string, string>;
    // EQ-VAS labels
    eqVasTopLabelValue: string;
    eqVasBottomLabelValue: string;
    eqVasValueBoxLabelValue: string;
    translatedEqVasTopLabelValue: string;
    translatedEqVasBottomLabelValue: string;
    translatedEqVasValueBoxLabelValue: string;
    // Scale labels
    scaleLabels: Array<{ value: number; baseLabel: string; translatedLabel: string }>;
  }>({
    questionLabel: '',
    answerLabels: {},
    nestedQuestionLabels: {},
    translatedQuestionLabel: '',
    translatedAnswerLabels: {},
    translatedNestedQuestionLabels: {},
    descInlineValue: '',
    descValue: '',
    exampleValue: '',
    translatedDescInlineValue: '',
    translatedDescValue: '',
    translatedExampleValue: '',
    translatedThirdPartyLabel: '',
    translatedThirdPartyDescInline: '',
    translatedThirdPartyDesc: '',
    translatedThirdPartyExample: '',
    translatedOptionThirdPartyLabels: {},
    eqVasTopLabelValue: '',
    eqVasBottomLabelValue: '',
    eqVasValueBoxLabelValue: '',
    translatedEqVasTopLabelValue: '',
    translatedEqVasBottomLabelValue: '',
    translatedEqVasValueBoxLabelValue: '',
    scaleLabels: [],
  });
  const latestSaveDataRef = useRef<Partial<QuestionData> | null>(null);

  // Update original labels when data is loaded
  useEffect(() => {
    if (questionWithLabels) {
      originalLabelsRef.current.questionLabel =
        questionWithLabels.label || questionWithLabels.questionKey || '';
      const labels: Record<string, string> = {};
      const { answerKeys } = questionWithLabels;
      const apiAnswerLabels = questionWithLabels.answerLabels as Record<string, string> | undefined;
      if (answerKeys && apiAnswerLabels) {
        answerKeys.forEach((key: string) => {
          labels[key] = apiAnswerLabels[key] || '';
        });
      }
      originalLabelsRef.current.answerLabels = labels;
      originalLabelsRef.current.nestedQuestionLabels = buildNestedQuestionLabelMap({
        questionKey,
        answerOptionsMap: questionWithLabels.answerOptionsMap as
          | Record<string, unknown>
          | undefined,
      });

      // V1과 동일: descInline, desc, example 원본 값 저장 (base locale)
      const apiData = questionWithLabels as {
        descriptionInline?: string;
        description?: string;
        example?: string;
      };
      originalLabelsRef.current.descInlineValue = apiData.descriptionInline || '';
      originalLabelsRef.current.descValue = apiData.description || '';
      originalLabelsRef.current.exampleValue = apiData.example || '';
    }
  }, [questionWithLabels, questionKey]);

  useEffect(() => {
    if (translatedLabelsData && currentLocale !== baseLocale) {
      originalLabelsRef.current.translatedQuestionLabel = translatedLabelsData.label || '';
      const labels: Record<string, string> = {};
      const { answerKeys } = translatedLabelsData;
      const apiAnswerLabels = translatedLabelsData.answerLabels as
        | Record<string, string>
        | undefined;
      if (answerKeys && apiAnswerLabels) {
        answerKeys.forEach((key: string) => {
          labels[key] = apiAnswerLabels[key] || '';
        });
      }
      originalLabelsRef.current.translatedAnswerLabels = labels;
      originalLabelsRef.current.translatedNestedQuestionLabels = buildNestedQuestionLabelMap({
        questionKey,
        answerOptionsMap: (translatedLabelsData as { answerOptionsMap?: Record<string, unknown> })
          .answerOptionsMap,
      });

      // V1과 동일: descInline, desc, example 원본 값 저장 (current locale)
      const apiData = translatedLabelsData as {
        descriptionInline?: string;
        description?: string;
        example?: string;
      };
      originalLabelsRef.current.translatedDescInlineValue = apiData.descriptionInline || '';
      originalLabelsRef.current.translatedDescValue = apiData.description || '';
      originalLabelsRef.current.translatedExampleValue = apiData.example || '';
      originalLabelsRef.current.translatedThirdPartyLabel = '';
      originalLabelsRef.current.translatedThirdPartyDescInline = '';
      originalLabelsRef.current.translatedThirdPartyDesc = '';
      originalLabelsRef.current.translatedThirdPartyExample = '';
      originalLabelsRef.current.translatedOptionThirdPartyLabels = {};
    }
  }, [translatedLabelsData, currentLocale, baseLocale, questionKey]);

  // Update mutation
  const updateMutation = useQuestionControllerUpdate({
    mutation: {
      onSuccess: () => {
        // Invalidate queries to refresh data
        // 1. 개별 질문 쿼리 무효화 (모달 재오픈 시 최신 데이터 로드)
        if (questionId) {
          queryClient.invalidateQueries({
            queryKey: getQuestionControllerFindByIdQueryKey(questionId),
          });
        }

        // 2. locale 파라미터와 관계없이 해당 treeId의 모든 쿼리를 무효화
        if (selectedTreeId) {
          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey;
              return Array.isArray(key) && key[0] === `/questions/tree/${selectedTreeId}`;
            },
          });
        }
      },
      onError: () => {
        toast.error('Failed to save question');
      },
    },
  });

  // Translation bulk upsert mutation
  const translationMutation = useTranslationControllerBulkUpsert({
    mutation: {
      onError: () => {
        toast.error('Failed to save translations');
      },
    },
  });

  // Translation search (for lock status)
  const translationSearchMutation = useTranslationControllerSearch({
    mutation: {
      onSuccess: (data) => {
        const translations = data.translations || [];
        const map: Record<string, TranslationResponseDto> = {};
        const lockedMap: Record<string, boolean> = {};
        translations.forEach((entry) => {
          if (!entry.key) return;
          const entryLocked =
            entry.locked === true || entry.status === 'locked' || entry.status === 'manual';
          const existing = map[entry.key];
          if (!existing) {
            map[entry.key] = entry;
          } else {
            const existingLocked =
              existing.locked === true ||
              existing.status === 'locked' ||
              existing.status === 'manual';
            if (!existingLocked && entryLocked) {
              map[entry.key] = entry;
            } else if (existingLocked === entryLocked && (entry.id ?? 0) > (existing.id ?? 0)) {
              map[entry.key] = entry;
            }
          }
          lockedMap[entry.key] = lockedMap[entry.key] === true || entryLocked;
        });
        translationMapRef.current = map;
        setTranslationLockedMap(lockedMap);
        originalTranslationLockedMapRef.current = { ...lockedMap };

        const answerKeysForStatus =
          questionData?.answerKeys || questionWithLabels?.answerKeys || [];
        const descInlineKey =
          questionResponse?.descInline || (questionKey ? `${questionKey}-desc-inline` : undefined);
        const descKey = questionResponse?.desc || (questionKey ? `${questionKey}-desc` : undefined);
        const exampleKey =
          questionResponse?.exampleKey ||
          (questionKey ? `${questionKey}-example-label-key` : undefined);

        const translationValues: Record<string, string> = {};
        Object.entries(map).forEach(([key, entry]) => {
          translationValues[key] = entry.value || '';
        });
        const translatedAnswerFallback: Record<string, string> = {};
        answerKeysForStatus.forEach((answerKey) => {
          translatedAnswerFallback[answerKey] = translatedAnswerLabels[answerKey] || '';
        });
        const translatedLabelsFallbackData = translatedLabelsData as
          | {
              label?: string;
              descriptionInline?: string;
              description?: string;
              example?: string;
            }
          | undefined;
        const resolvedTranslated = resolveQuestionLocaleValues({
          questionKey,
          answerKeys: answerKeysForStatus,
          descInlineKey,
          descKey,
          exampleKey,
          fallbackQuestionLabel:
            translatedQuestionLabel || translatedLabelsFallbackData?.label || '',
          fallbackAnswerLabels: translatedAnswerFallback,
          fallbackDescInlineValue:
            translatedDescInlineValue || translatedLabelsFallbackData?.descriptionInline || '',
          fallbackDescValue: translatedDescValue || translatedLabelsFallbackData?.description || '',
          fallbackExampleValue:
            translatedExampleValue || translatedLabelsFallbackData?.example || '',
          translationValues,
        });
        setTranslatedQuestionLabel(resolvedTranslated.questionLabel);
        setTranslatedAnswerLabels(resolvedTranslated.answerLabels);
        setTranslatedDescInlineValue(resolvedTranslated.descInlineValue);
        setTranslatedDescValue(resolvedTranslated.descValue);
        setTranslatedExampleValue(resolvedTranslated.exampleValue);
        const nestedQuestionEntries = collectNestedQuestionTranslationEntries({
          questionKey,
          answerOptionsMap: ((questionData?.answerOptionsMap as
            | Record<string, unknown>
            | undefined) ||
            (questionWithLabels?.answerOptionsMap as Record<string, unknown> | undefined) ||
            null) as Record<
            string,
            { question?: string; questionIndex?: number; order?: number } | undefined
          > | null,
        });
        const fallbackNestedQuestionLabels = buildNestedQuestionLabelMap({
          questionKey,
          answerOptionsMap: ((
            translatedLabelsData as { answerOptionsMap?: Record<string, unknown> } | undefined
          )?.answerOptionsMap ||
            (localizedQuestionForCurrentLocale?.answerOptionsMap as
              | Record<string, unknown>
              | undefined) ||
            (questionData?.answerOptionsMap as Record<string, unknown> | undefined) ||
            undefined) as Record<string, unknown> | undefined,
        });
        const resolvedNestedQuestionLabels = nestedQuestionEntries.reduce<Record<number, string>>(
          (acc, entry) => {
            acc[entry.questionIndex] =
              translationValues[entry.key] ||
              fallbackNestedQuestionLabels[entry.questionIndex] ||
              '';
            return acc;
          },
          {},
        );
        setTranslatedNestedQuestionLabels(resolvedNestedQuestionLabels);

        if (!questionKey) {
          setTranslatedThirdPartyLabel('');
          setTranslatedThirdPartyDescInline('');
          setTranslatedThirdPartyDesc('');
          setTranslatedThirdPartyExample('');
          setTranslatedOptionThirdPartyLabels({});
          setTranslatedNestedQuestionLabels({});
          return;
        }

        const thirdPartyLabelKey = genQuestionThirdPartyLabelKey(questionKey);
        const thirdPartyDescInlineKey = genQuestionThirdPartyDescInlineKey(questionKey);
        const thirdPartyDescKey = genQuestionThirdPartyDescKey(questionKey);
        const thirdPartyExampleKey = genQuestionThirdPartyExampleKey(questionKey);

        const nextTranslatedThirdPartyLabel = map[thirdPartyLabelKey]?.value || '';
        const nextTranslatedThirdPartyDescInline = map[thirdPartyDescInlineKey]?.value || '';
        const nextTranslatedThirdPartyDesc = map[thirdPartyDescKey]?.value || '';
        const nextTranslatedThirdPartyExample = map[thirdPartyExampleKey]?.value || '';

        const optionThirdPartyMap: Record<string, string> = {};
        answerKeysForStatus.forEach((optionKey) => {
          const translationKey = genOptionThirdPartyLabelKey(questionKey, optionKey);
          const value = map[translationKey]?.value;
          if (!isTrimEmpty(value)) {
            optionThirdPartyMap[optionKey] = value || '';
          }
        });

        setTranslatedThirdPartyLabel(nextTranslatedThirdPartyLabel);
        setTranslatedThirdPartyDescInline(nextTranslatedThirdPartyDescInline);
        setTranslatedThirdPartyDesc(nextTranslatedThirdPartyDesc);
        setTranslatedThirdPartyExample(nextTranslatedThirdPartyExample);
        setTranslatedOptionThirdPartyLabels(optionThirdPartyMap);
        originalLabelsRef.current.translatedNestedQuestionLabels = {
          ...resolvedNestedQuestionLabels,
        };

        originalLabelsRef.current.translatedThirdPartyLabel = nextTranslatedThirdPartyLabel;
        originalLabelsRef.current.translatedThirdPartyDescInline =
          nextTranslatedThirdPartyDescInline;
        originalLabelsRef.current.translatedThirdPartyDesc = nextTranslatedThirdPartyDesc;
        originalLabelsRef.current.translatedThirdPartyExample = nextTranslatedThirdPartyExample;
        originalLabelsRef.current.translatedOptionThirdPartyLabels = optionThirdPartyMap;

        // EQ-VAS labels from translation search (both base and translated)
        const eqVasTopKey = genEqVasTopLabelKey(questionKey);
        const eqVasBottomKey = genEqVasBottomLabelKey(questionKey);
        const eqVasValueBoxKey = genEqVasValueBoxLabelKey(questionKey);
        const nextEqVasTopLabel = map[eqVasTopKey]?.value || '';
        const nextEqVasBottomLabel = map[eqVasBottomKey]?.value || '';
        const nextEqVasValueBoxLabel = map[eqVasValueBoxKey]?.value || '';
        // If current locale is base locale, set base values; otherwise set translated values
        if (currentLocale === baseLocale) {
          setEqVasTopLabelValue(nextEqVasTopLabel);
          setEqVasBottomLabelValue(nextEqVasBottomLabel);
          setEqVasValueBoxLabelValue(nextEqVasValueBoxLabel);
          originalLabelsRef.current.eqVasTopLabelValue = nextEqVasTopLabel;
          originalLabelsRef.current.eqVasBottomLabelValue = nextEqVasBottomLabel;
          originalLabelsRef.current.eqVasValueBoxLabelValue = nextEqVasValueBoxLabel;
        } else {
          setTranslatedEqVasTopLabelValue(nextEqVasTopLabel);
          setTranslatedEqVasBottomLabelValue(nextEqVasBottomLabel);
          setTranslatedEqVasValueBoxLabelValue(nextEqVasValueBoxLabel);
          originalLabelsRef.current.translatedEqVasTopLabelValue = nextEqVasTopLabel;
          originalLabelsRef.current.translatedEqVasBottomLabelValue = nextEqVasBottomLabel;
          originalLabelsRef.current.translatedEqVasValueBoxLabelValue = nextEqVasValueBoxLabel;
        }

        // Scale labels from translation search
        if (questionKey) {
          const scaleLabelValsSearch: number[] = questionData?.typeOptions?.scaleLabels || [];
          if (currentLocale === baseLocale) {
            const loadedScaleLabels = scaleLabelValsSearch.map((val: number) => {
              const key = genScaleLabelKey(questionKey, val);
              return { value: val, baseLabel: map[key]?.value || '', translatedLabel: '' };
            });
            setScaleLabels(loadedScaleLabels);
            setShowScaleLabels(loadedScaleLabels.length > 0);
            originalLabelsRef.current.scaleLabels = loadedScaleLabels.map((l) => ({ ...l }));
          } else {
            setScaleLabels((prev) =>
              prev.map((entry) => {
                const key = genScaleLabelKey(questionKey, entry.value);
                return { ...entry, translatedLabel: map[key]?.value || '' };
              })
            );
            originalLabelsRef.current.scaleLabels = originalLabelsRef.current.scaleLabels.map((entry) => {
              const key = genScaleLabelKey(questionKey, entry.value);
              return { ...entry, translatedLabel: map[key]?.value || '' };
            });
          }
        }
      },
      onError: () => {
        translationMapRef.current = {};
        setTranslationLockedMap({});
        originalTranslationLockedMapRef.current = {};
        setTranslatedThirdPartyLabel('');
        setTranslatedThirdPartyDescInline('');
        setTranslatedThirdPartyDesc('');
        setTranslatedThirdPartyExample('');
        setTranslatedOptionThirdPartyLabels({});
        originalLabelsRef.current.translatedThirdPartyLabel = '';
        originalLabelsRef.current.translatedThirdPartyDescInline = '';
        originalLabelsRef.current.translatedThirdPartyDesc = '';
        originalLabelsRef.current.translatedThirdPartyExample = '';
        originalLabelsRef.current.translatedOptionThirdPartyLabels = {};
      },
    },
  });

  // File upload mutation
  const uploadFileMutation = useFileControllerUploadFile();

  // Link image to question mutation
  const linkImageMutation = useFileControllerLinkImageToQuestion();

  // Unlink image from question mutation
  const unlinkImageMutation = useFileControllerUnlinkImageFromQuestion();

  // Open modal
  const handleOpen = useCallback((qKey: string, qId: number) => {
    setQuestionKey(qKey);
    setQuestionId(qId);
    setIsOpen(true);
  }, []);

  // Close modal
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setQuestionKey(undefined);
    setQuestionId(undefined);
    setQuestionLabel('');
    setAnswerLabels({});
    setTranslatedQuestionLabel('');
    setTranslatedAnswerLabels({});
    setTranslatedNestedQuestionLabels({});
    setCurrentLocale(resolvedSelectedLocale);
    setTranslationLockedMap({});
    translationMapRef.current = {};
    originalTranslationLockedMapRef.current = {};
    // desc, descInline, example 초기화
    setDescInlineValue('');
    setDescValue('');
    setExampleValue('');
    setTranslatedDescInlineValue('');
    setTranslatedDescValue('');
    setTranslatedExampleValue('');
    setTranslatedThirdPartyLabel('');
    setTranslatedThirdPartyDescInline('');
    setTranslatedThirdPartyDesc('');
    setTranslatedThirdPartyExample('');
    setTranslatedOptionThirdPartyLabels({});
    // EQ-VAS 라벨 초기화
    setEqVasTopLabelValue('');
    setEqVasBottomLabelValue('');
    setEqVasValueBoxLabelValue('');
    setTranslatedEqVasTopLabelValue('');
    setTranslatedEqVasBottomLabelValue('');
    setTranslatedEqVasValueBoxLabelValue('');
    setScaleLabels([]);
    setShowScaleLabels(false);
    // 이미지 상태 초기화
    setImageUrl(null);
    setIsUploading(false);
  }, [resolvedSelectedLocale]);

  // Label change handlers (base locale)
  const handleLabelChange = useCallback((value: string) => {
    setQuestionLabel(value);
  }, []);

  const handleAnswerLabelChange = useCallback((key: string, label: string) => {
    setAnswerLabels((prev) => ({ ...prev, [key]: label }));
  }, []);

  // Translated label change handlers (current locale)
  const handleTranslatedLabelChange = useCallback((value: string) => {
    setTranslatedQuestionLabel(value);
  }, []);

  const handleTranslatedAnswerLabelChange = useCallback((key: string, label: string) => {
    setTranslatedAnswerLabels((prev) => ({ ...prev, [key]: label }));
  }, []);

  const handleTranslatedNestedQuestionLabelChange = useCallback(
    (questionIndex: number, label: string) => {
      setTranslatedNestedQuestionLabels((prev) => ({
        ...prev,
        [questionIndex]: label,
      }));
    },
    [],
  );

  // Desc, DescInline, Example 변경 핸들러 (base locale)
  const handleDescInlineChange = useCallback((value: string) => {
    setDescInlineValue(value);
  }, []);

  const handleDescChange = useCallback((value: string) => {
    setDescValue(value);
  }, []);

  const handleExampleChange = useCallback((value: string) => {
    setExampleValue(value);
  }, []);

  // Desc, DescInline, Example 변경 핸들러 (current locale)
  const handleTranslatedDescInlineChange = useCallback((value: string) => {
    setTranslatedDescInlineValue(value);
  }, []);

  const handleTranslatedDescChange = useCallback((value: string) => {
    setTranslatedDescValue(value);
  }, []);

  const handleTranslatedExampleChange = useCallback((value: string) => {
    setTranslatedExampleValue(value);
  }, []);

  const handleTranslatedThirdPartyLabelChange = useCallback((value: string) => {
    setTranslatedThirdPartyLabel(value);
  }, []);

  const handleTranslatedThirdPartyDescInlineChange = useCallback((value: string) => {
    setTranslatedThirdPartyDescInline(value);
  }, []);

  const handleTranslatedThirdPartyDescChange = useCallback((value: string) => {
    setTranslatedThirdPartyDesc(value);
  }, []);

  const handleTranslatedThirdPartyExampleChange = useCallback((value: string) => {
    setTranslatedThirdPartyExample(value);
  }, []);

  const handleTranslatedOptionThirdPartyLabelChange = useCallback(
    (optionKey: string, value: string) => {
      setTranslatedOptionThirdPartyLabels((prev) => ({ ...prev, [optionKey]: value }));
    },
    [],
  );

  // EQ-VAS label change handlers (base locale)
  const handleEqVasTopLabelChange = useCallback((value: string) => {
    setEqVasTopLabelValue(value);
  }, []);

  const handleEqVasBottomLabelChange = useCallback((value: string) => {
    setEqVasBottomLabelValue(value);
  }, []);

  const handleEqVasValueBoxLabelChange = useCallback((value: string) => {
    setEqVasValueBoxLabelValue(value);
  }, []);

  // EQ-VAS label change handlers (current locale)
  const handleTranslatedEqVasTopLabelChange = useCallback((value: string) => {
    setTranslatedEqVasTopLabelValue(value);
  }, []);

  const handleTranslatedEqVasBottomLabelChange = useCallback((value: string) => {
    setTranslatedEqVasBottomLabelValue(value);
  }, []);

  const handleTranslatedEqVasValueBoxLabelChange = useCallback((value: string) => {
    setTranslatedEqVasValueBoxLabelValue(value);
  }, []);

  // Scale label handlers
  const handleAddScaleLabel = useCallback(() => {
    // Use questionData first, fall back to questionWithLabels for typeOptions
    const typeOpts = questionData?.typeOptions || (questionWithLabels as Record<string, unknown> | undefined)?.typeOptions as Record<string, unknown> | undefined;
    if (!typeOpts) return; // Guard: don't add labels until question data is loaded
    const min = Number(typeOpts.min ?? 1);
    const max = Number(typeOpts.max ?? 10);
    const step = Number(typeOpts.step ?? 1);
    const usedValues = new Set(scaleLabels.map((l) => l.value));
    let nextValue: number | null = null;
    for (let v = min; v <= max; v += step) {
      if (!usedValues.has(v)) { nextValue = v; break; }
    }
    if (nextValue === null) return;
    setScaleLabels((prev) => [...prev, { value: nextValue!, baseLabel: '', translatedLabel: '' }]);
  }, [questionData?.typeOptions, questionWithLabels, scaleLabels]);

  const handleRemoveScaleLabel = useCallback((value: number) => {
    setScaleLabels((prev) => prev.filter((l) => l.value !== value));
  }, []);

  const handleScaleLabelValueChange = useCallback((oldValue: number, newValue: number) => {
    setScaleLabels((prev) => prev.map((l) => (l.value === oldValue ? { ...l, value: newValue } : l)));
  }, []);

  const handleScaleLabelTextChange = useCallback((value: number, text: string) => {
    setScaleLabels((prev) => prev.map((l) => (l.value === value ? { ...l, baseLabel: text } : l)));
  }, []);

  const handleScaleLabelTranslatedTextChange = useCallback((value: number, text: string) => {
    setScaleLabels((prev) => prev.map((l) => (l.value === value ? { ...l, translatedLabel: text } : l)));
  }, []);

  // Image upload handler
  const handleImageUpload = useCallback(
    async (file: File) => {
      if (!questionId) return;

      setIsUploading(true);
      try {
        // 1. 파일 업로드
        const uploadResponse = await uploadFileMutation.mutateAsync({
          data: { file },
        });

        const uploadedFile = uploadResponse.file;

        // 2. 기존 이미지 연결 해제
        await unlinkImageMutation.mutateAsync({ questionId });

        // 3. 새 이미지를 질문에 연결
        await linkImageMutation.mutateAsync({ questionId, fileId: uploadedFile.id });

        // 4. 이미지 쿼리 무효화 (자동으로 imageUrl 갱신)
        queryClient.invalidateQueries({
          queryKey: getFileControllerGetQuestionImagesQueryKey(questionId),
        });

        toast.success('Image uploaded successfully');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to upload image:', error);
        toast.error('Failed to upload image');
      } finally {
        setIsUploading(false);
      }
    },
    [questionId, uploadFileMutation, unlinkImageMutation, linkImageMutation, queryClient],
  );

  // Image remove handler
  const handleImageRemove = useCallback(async () => {
    if (!questionId) return;

    try {
      await unlinkImageMutation.mutateAsync({ questionId });

      // 이미지 쿼리 무효화 (자동으로 imageUrl 갱신)
      queryClient.invalidateQueries({
        queryKey: getFileControllerGetQuestionImagesQueryKey(questionId),
      });

      toast.success('Image removed');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to remove image:', error);
      toast.error('Failed to remove image');
    }
  }, [questionId, unlinkImageMutation, queryClient]);

  // Locale change handler
  const handleLocaleChange = useCallback(
    (newLocale: string) => {
      const normalizedLocale = normalizeLocale(newLocale);
      setCurrentLocale(normalizedLocale);
      onLocaleChange?.(normalizedLocale);
    },
    [onLocaleChange],
  );

  const handleTranslationLockChange = useCallback(
    async (key: string, locked: boolean) => {
      if (!key) return;
      setTranslationLockedMap((prev) => ({
        ...prev,
        [key]: locked,
      }));

      if (!surveyId || currentLocale === baseLocale) return;

      const numericSurveyId = Number(surveyId);
      const answerKeyList = questionData?.answerKeys || questionWithLabels?.answerKeys || [];
      const isOptionKey = answerKeyList.includes(key);
      const descInlineKey =
        questionResponse?.descInline || (questionKey ? `${questionKey}-desc-inline` : undefined);
      const descKey = questionResponse?.desc || (questionKey ? `${questionKey}-desc` : undefined);
      const exampleKey =
        questionResponse?.exampleKey ||
        (questionKey ? `${questionKey}-example-label-key` : undefined);

      let dataType: UpsertTranslationDto['dataType'] = 'qsLabel';
      let englishValue = questionLabel;
      let translatedValue = translatedQuestionLabel;

      if (isOptionKey) {
        dataType = 'option';
        englishValue = answerLabels[key] || key;
        translatedValue = translatedAnswerLabels[key] || '';
      } else if (descInlineKey && key === descInlineKey) {
        englishValue = descInlineValue;
        translatedValue = translatedDescInlineValue;
      } else if (descKey && key === descKey) {
        englishValue = descValue;
        translatedValue = translatedDescValue;
      } else if (exampleKey && key === exampleKey) {
        englishValue = exampleValue;
        translatedValue = translatedExampleValue;
      }

      const existing = translationMapRef.current[key];
      const valueToSave =
        locked && isTrimEmpty(translatedValue)
          ? englishValue
          : translatedValue || existing?.value || '';

      try {
        await translationMutation.mutateAsync({
          data: {
            translations: [
              {
                surveyId: numericSurveyId,
                key,
                value: valueToSave,
                locale: currentLocale,
                type: 'patient',
                dataType,
                msgid: isTrimEmpty(englishValue) ? undefined : englishValue,
                locked,
                status: locked ? 'locked' : 'auto',
              },
            ],
          },
        });
        if (existing) {
          translationMapRef.current[key] = {
            ...existing,
            locked,
            value: valueToSave,
            msgid: existing.msgid,
            status: locked ? 'locked' : 'auto',
          };
        }
        originalTranslationLockedMapRef.current = {
          ...originalTranslationLockedMapRef.current,
          [key]: locked,
        };
      } catch (error) {
        setTranslationLockedMap((prev) => ({
          ...prev,
          [key]: !locked,
        }));
      }
    },
    [
      surveyId,
      currentLocale,
      baseLocale,
      questionData?.answerKeys,
      questionWithLabels?.answerKeys,
      answerLabels,
      questionLabel,
      translatedAnswerLabels,
      translatedQuestionLabel,
      translationMutation,
    ],
  );

  // Convert QuestionData to UpdateQuestionDto
  const convertToUpdateDto = useCallback((data: Partial<QuestionData>): UpdateQuestionDto => {
    const dto: UpdateQuestionDto = {};

    if (data.type !== undefined) dto.type = data.type as UpdateQuestionDto['type'];
    if (data.required !== undefined) dto.required = data.required;
    if (data.enabled !== undefined) dto.enabled = data.enabled;
    // desc, descInline, exampleKey: null을 그대로 전송해야 백엔드에서 삭제 처리됨
    // null → '' (빈 문자열)로 변환하여 전송
    if (data.desc !== undefined) dto.desc = data.desc === null ? '' : data.desc;
    if (data.descInline !== undefined)
      dto.descInline = data.descInline === null ? '' : data.descInline;
    if (data.exampleKey !== undefined)
      dto.exampleKey = data.exampleKey === null ? '' : data.exampleKey;
    if (data.answerKeys !== undefined) dto.answerKeys = data.answerKeys;
    if (data.showConditions !== undefined) dto.showConditions = data.showConditions;
    if (data.typeOptions !== undefined) dto.typeOptions = data.typeOptions ?? undefined;
    if (data.minSelectedOptions !== undefined)
      dto.minSelectedOptions = data.minSelectedOptions ?? undefined;
    if (data.maxSelectedOptions !== undefined)
      dto.maxSelectedOptions = data.maxSelectedOptions ?? undefined;
    if (data.maxLength !== undefined) dto.maxLength = data.maxLength ?? undefined;
    if (data.global !== undefined) dto.global = data.global;
    if (data.systemKey !== undefined) dto.systemKey = data.systemKey ?? undefined;
    if (data.reportLabel !== undefined) dto.reportLabel = data.reportLabel ?? undefined;
    if (data.answerOptionsMap !== undefined)
      dto.answerOptionsMap = data.answerOptionsMap ?? undefined;
    if (data.isThirdParty !== undefined) dto.isThirdParty = data.isThirdParty;
    if (data.thirdPartyLabel !== undefined)
      dto.thirdPartyLabel = data.thirdPartyLabel === null ? '' : data.thirdPartyLabel;
    if (data.thirdPartyDescInline !== undefined)
      dto.thirdPartyDescInline =
        data.thirdPartyDescInline === null ? '' : data.thirdPartyDescInline;
    if (data.thirdPartyDesc !== undefined)
      dto.thirdPartyDesc = data.thirdPartyDesc === null ? '' : data.thirdPartyDesc;
    if (data.thirdPartyExample !== undefined)
      dto.thirdPartyExample = data.thirdPartyExample === null ? '' : data.thirdPartyExample;
    if (data.optionConditions !== undefined)
      dto.optionConditions = (data.optionConditions as UpdateQuestionDto['optionConditions']) ?? undefined;

    return dto;
  }, []);

  // Build translations to save (only changed ones)
  // 저장 시 사용할 번역 키 생성 (새로 입력하는 경우에도 키 생성)
  const getDescInlineKeyForSave = useCallback(
    (hasValue: boolean): string | null => {
      if (!questionKey || !hasValue) return null;
      // 기존 키가 있으면 사용, 없으면 새로 생성
      return questionResponse?.descInline || `${questionKey}-desc-inline`;
    },
    [questionKey, questionResponse?.descInline],
  );

  const getDescKeyForSave = useCallback(
    (hasValue: boolean): string | null => {
      if (!questionKey || !hasValue) return null;
      return questionResponse?.desc || `${questionKey}-desc`;
    },
    [questionKey, questionResponse?.desc],
  );

  const getExampleKeyForSave = useCallback(
    (hasValue: boolean): string | null => {
      if (!questionKey || !hasValue) return null;
      return questionResponse?.exampleKey || `${questionKey}-example-label-key`;
    },
    [questionKey, questionResponse?.exampleKey],
  );

  const translationKeys = useMemo(() => {
    if (!questionKey) return [];
    const keys = new Set<string>();
    keys.add(questionKey);
    const answerKeysForStatus = questionData?.answerKeys || questionWithLabels?.answerKeys || [];
    answerKeysForStatus.forEach((key) => {
      if (key) keys.add(key);
    });
    const descInlineKey =
      questionResponse?.descInline || (questionKey ? `${questionKey}-desc-inline` : undefined);
    const descKey = questionResponse?.desc || (questionKey ? `${questionKey}-desc` : undefined);
    const exampleKey =
      questionResponse?.exampleKey ||
      (questionKey ? `${questionKey}-example-label-key` : undefined);
    if (descInlineKey) keys.add(descInlineKey);
    if (descKey) keys.add(descKey);
    if (exampleKey) keys.add(exampleKey);

    // 3rd party question-level translation keys
    keys.add(genQuestionThirdPartyLabelKey(questionKey));
    keys.add(genQuestionThirdPartyDescInlineKey(questionKey));
    keys.add(genQuestionThirdPartyDescKey(questionKey));
    keys.add(genQuestionThirdPartyExampleKey(questionKey));

    // 3rd party option-level translation keys
    answerKeysForStatus.forEach((optionKey) => {
      if (!optionKey) return;
      keys.add(genOptionThirdPartyLabelKey(questionKey, optionKey));
    });

    // EQ-VAS specific label keys (always include for eq_vas type questions)
    keys.add(genEqVasTopLabelKey(questionKey));
    keys.add(genEqVasBottomLabelKey(questionKey));
    keys.add(genEqVasValueBoxLabelKey(questionKey));

    // Scale label keys
    const scaleLabelValues: number[] = questionData?.typeOptions?.scaleLabels || [];
    scaleLabelValues.forEach((val: number) => {
      keys.add(genScaleLabelKey(questionKey, val));
    });
    scaleLabels.forEach((entry) => {
      keys.add(genScaleLabelKey(questionKey, entry.value));
    });

    const nestedQuestionEntries = collectNestedQuestionTranslationEntries({
      questionKey,
      answerOptionsMap: ((questionData?.answerOptionsMap as Record<string, unknown> | undefined) ||
        (questionWithLabels?.answerOptionsMap as Record<string, unknown> | undefined) ||
        null) as Record<
        string,
        { question?: string; questionIndex?: number; order?: number } | undefined
      > | null,
    });
    nestedQuestionEntries.forEach((entry) => {
      keys.add(entry.key);
    });

    return Array.from(keys);
  }, [
    questionKey,
    questionData?.answerKeys,
    questionData?.answerOptionsMap,
    questionData?.typeOptions,
    questionWithLabels?.answerKeys,
    questionWithLabels?.answerOptionsMap,
    questionResponse?.descInline,
    questionResponse?.desc,
    questionResponse?.exampleKey,
    scaleLabels,
  ]);

  // Fetch translation status for current locale
  useEffect(() => {
    if (!isOpen || !surveyId || currentLocale === baseLocale) {
      const hasLockedState =
        Object.keys(translationLockedMap).length > 0 ||
        Object.keys(originalTranslationLockedMapRef.current).length > 0 ||
        Object.keys(translationMapRef.current).length > 0 ||
        translationSearchSignatureRef.current !== '';
      if (!hasLockedState) {
        return;
      }
      translationMapRef.current = {};
      setTranslationLockedMap({});
      originalTranslationLockedMapRef.current = {};
      translationSearchSignatureRef.current = '';
      setTranslatedThirdPartyLabel('');
      setTranslatedThirdPartyDescInline('');
      setTranslatedThirdPartyDesc('');
      setTranslatedThirdPartyExample('');
      setTranslatedOptionThirdPartyLabels({});
      setTranslatedNestedQuestionLabels({});
      return;
    }
    if (translationKeys.length === 0) return;
    const signature = JSON.stringify({
      surveyId: Number(surveyId),
      locale: currentLocale,
      keys: [...translationKeys].sort(),
    });
    if (translationSearchSignatureRef.current === signature) return;
    translationSearchSignatureRef.current = signature;
    translationSearchMutation.mutate({
      data: {
        surveyId: Number(surveyId),
        keys: translationKeys,
        locale: currentLocale,
        type: 'patient',
      },
    });
  }, [isOpen, surveyId, currentLocale, baseLocale, translationKeys, translationSearchMutation]);

  const buildTranslationsToSave = useCallback((): UpsertTranslationDto[] => {
    if (!surveyId || !questionKey) return [];

    const translations: UpsertTranslationDto[] = [];
    const numericSurveyId = Number(surveyId);
    // Base locale translations
    // Question label
    if (questionLabel !== originalLabelsRef.current.questionLabel) {
      translations.push({
        surveyId: numericSurveyId,
        key: questionKey,
        value: questionLabel,
        locale: baseLocale,
        type: 'patient',
        dataType: 'qsLabel',
      });
    }

    // Answer labels (base locale)
    Object.entries(answerLabels).forEach(([key, value]) => {
      if (value !== originalLabelsRef.current.answerLabels[key]) {
        translations.push({
          surveyId: numericSurveyId,
          key,
          value,
          locale: baseLocale,
          type: 'patient',
          dataType: 'option',
        });
      }
    });

    // Desc Inline (base locale) - V1과 동일하게 번역 키로 저장 (dataType: qsLabel)
    const descInlineKeyToSave = getDescInlineKeyForSave(!isTrimEmpty(descInlineValue));
    if (descInlineKeyToSave && descInlineValue !== originalLabelsRef.current.descInlineValue) {
      translations.push({
        surveyId: numericSurveyId,
        key: descInlineKeyToSave,
        value: descInlineValue,
        locale: baseLocale,
        type: 'patient',
        dataType: 'qsLabel',
      });
    }

    // Desc (base locale)
    const descKeyToSave = getDescKeyForSave(!isTrimEmpty(descValue));
    if (descKeyToSave && descValue !== originalLabelsRef.current.descValue) {
      translations.push({
        surveyId: numericSurveyId,
        key: descKeyToSave,
        value: descValue,
        locale: baseLocale,
        type: 'patient',
        dataType: 'qsLabel',
      });
    }

    // Example (base locale)
    const exampleKeyToSave = getExampleKeyForSave(!isTrimEmpty(exampleValue));
    if (exampleKeyToSave && exampleValue !== originalLabelsRef.current.exampleValue) {
      translations.push({
        surveyId: numericSurveyId,
        key: exampleKeyToSave,
        value: exampleValue,
        locale: baseLocale,
        type: 'patient',
        dataType: 'qsLabel',
      });
    }

    // EQ-VAS labels (base locale)
    const eqVasTopKeyToSave = genEqVasTopLabelKey(questionKey);
    const eqVasBottomKeyToSave = genEqVasBottomLabelKey(questionKey);
    const eqVasValueBoxKeyToSave = genEqVasValueBoxLabelKey(questionKey);
    if (!isTrimEmpty(eqVasTopLabelValue) && eqVasTopLabelValue !== originalLabelsRef.current.eqVasTopLabelValue) {
      translations.push({
        surveyId: numericSurveyId,
        key: eqVasTopKeyToSave,
        value: eqVasTopLabelValue,
        locale: baseLocale,
        type: 'patient',
        dataType: 'qsLabel',
      });
    }
    if (!isTrimEmpty(eqVasBottomLabelValue) && eqVasBottomLabelValue !== originalLabelsRef.current.eqVasBottomLabelValue) {
      translations.push({
        surveyId: numericSurveyId,
        key: eqVasBottomKeyToSave,
        value: eqVasBottomLabelValue,
        locale: baseLocale,
        type: 'patient',
        dataType: 'qsLabel',
      });
    }
    if (!isTrimEmpty(eqVasValueBoxLabelValue) && eqVasValueBoxLabelValue !== originalLabelsRef.current.eqVasValueBoxLabelValue) {
      translations.push({
        surveyId: numericSurveyId,
        key: eqVasValueBoxKeyToSave,
        value: eqVasValueBoxLabelValue,
        locale: baseLocale,
        type: 'patient',
        dataType: 'qsLabel',
      });
    }

    // Scale labels (base locale)
    scaleLabels.forEach((entry) => {
      const key = genScaleLabelKey(questionKey, entry.value);
      const original = originalLabelsRef.current.scaleLabels.find((o) => o.value === entry.value);
      if (entry.baseLabel !== (original?.baseLabel || '')) {
        translations.push({
          surveyId: numericSurveyId,
          key,
          value: entry.baseLabel,
          locale: baseLocale,
          type: 'patient',
          dataType: 'qsLabel',
        });
      }
    });

    const latestSaveData = latestSaveDataRef.current;
    const effectiveAnswerOptionsMap = ((latestSaveData?.answerOptionsMap as
      | Record<string, unknown>
      | undefined) ||
      (questionData?.answerOptionsMap as Record<string, unknown> | undefined) ||
      {}) as Record<string, unknown>;
    const effectiveAnswerKeys =
      latestSaveData?.answerKeys ||
      questionData?.answerKeys ||
      questionWithLabels?.answerKeys ||
      [];
    const nestedQuestionEntries = collectNestedQuestionTranslationEntries({
      questionKey,
      answerOptionsMap: effectiveAnswerOptionsMap as Record<
        string,
        { question?: string; questionIndex?: number; order?: number } | undefined
      >,
    });
    const englishThirdPartyLabel =
      latestSaveData?.thirdPartyLabel ?? questionData?.thirdPartyLabel ?? '';
    const englishThirdPartyDescInline =
      latestSaveData?.thirdPartyDescInline ?? questionData?.thirdPartyDescInline ?? '';
    const englishThirdPartyDesc =
      latestSaveData?.thirdPartyDesc ?? questionData?.thirdPartyDesc ?? '';
    const englishThirdPartyExample =
      latestSaveData?.thirdPartyExample ?? questionData?.thirdPartyExample ?? '';

    // Nested question labels (base locale)
    nestedQuestionEntries.forEach((entry) => {
      const originalValue =
        originalLabelsRef.current.nestedQuestionLabels[entry.questionIndex] || '';
      if (entry.question === originalValue) {
        return;
      }
      translations.push({
        surveyId: numericSurveyId,
        key: entry.key,
        value: entry.question,
        locale: baseLocale,
        type: 'patient',
        dataType: 'qsLabel',
      });
    });

    // Translated labels (current locale, if not base locale)
    if (currentLocale !== baseLocale) {
      const pushTranslation = ({
        key,
        value,
        originalValue,
        englishValue,
        dataType,
      }: {
        key: string;
        value: string;
        originalValue: string;
        englishValue: string;
        dataType: 'qsLabel' | 'option';
      }) => {
        const existing = translationMapRef.current[key];
        const valueChanged = value !== originalValue;
        const locked = translationLockedMap[key] === true;
        const originalLocked = originalTranslationLockedMapRef.current[key] === true;
        const lockChanged = locked !== originalLocked;
        if (!valueChanged && !lockChanged) {
          return;
        }
        let valueToSave = valueChanged ? value : existing?.value ?? value;
        if (isTrimEmpty(valueToSave) && locked) {
          valueToSave = englishValue;
        }
        translations.push({
          surveyId: numericSurveyId,
          key,
          value: valueToSave,
          locale: currentLocale,
          type: 'patient',
          dataType,
          msgid: isTrimEmpty(englishValue) ? undefined : englishValue,
          locked,
        });
      };

      // Translated question label
      pushTranslation({
        key: questionKey,
        value: translatedQuestionLabel,
        originalValue: originalLabelsRef.current.translatedQuestionLabel,
        englishValue: questionLabel,
        dataType: 'qsLabel',
      });

      // Translated answer labels
      Object.entries(translatedAnswerLabels).forEach(([key, value]) => {
        pushTranslation({
          key,
          value,
          originalValue: originalLabelsRef.current.translatedAnswerLabels[key] || '',
          englishValue: answerLabels[key] || key,
          dataType: 'option',
        });
      });

      nestedQuestionEntries.forEach((entry) => {
        pushTranslation({
          key: entry.key,
          value: translatedNestedQuestionLabels[entry.questionIndex] || '',
          originalValue:
            originalLabelsRef.current.translatedNestedQuestionLabels[entry.questionIndex] || '',
          englishValue: entry.question || '',
          dataType: 'qsLabel',
        });
      });

      // Translated Desc Inline
      if (descInlineKeyToSave) {
        pushTranslation({
          key: descInlineKeyToSave,
          value: translatedDescInlineValue,
          originalValue: originalLabelsRef.current.translatedDescInlineValue,
          englishValue: descInlineValue,
          dataType: 'qsLabel',
        });
      }

      // Translated Desc
      if (descKeyToSave) {
        pushTranslation({
          key: descKeyToSave,
          value: translatedDescValue,
          originalValue: originalLabelsRef.current.translatedDescValue,
          englishValue: descValue,
          dataType: 'qsLabel',
        });
      }

      // Translated Example
      if (exampleKeyToSave) {
        pushTranslation({
          key: exampleKeyToSave,
          value: translatedExampleValue,
          originalValue: originalLabelsRef.current.translatedExampleValue,
          englishValue: exampleValue,
          dataType: 'qsLabel',
        });
      }

      // Translated EQ-VAS labels
      if (!isTrimEmpty(eqVasTopLabelValue) || !isTrimEmpty(translatedEqVasTopLabelValue)) {
        pushTranslation({
          key: eqVasTopKeyToSave,
          value: translatedEqVasTopLabelValue,
          originalValue: originalLabelsRef.current.translatedEqVasTopLabelValue,
          englishValue: eqVasTopLabelValue,
          dataType: 'qsLabel',
        });
      }
      if (!isTrimEmpty(eqVasBottomLabelValue) || !isTrimEmpty(translatedEqVasBottomLabelValue)) {
        pushTranslation({
          key: eqVasBottomKeyToSave,
          value: translatedEqVasBottomLabelValue,
          originalValue: originalLabelsRef.current.translatedEqVasBottomLabelValue,
          englishValue: eqVasBottomLabelValue,
          dataType: 'qsLabel',
        });
      }
      if (!isTrimEmpty(eqVasValueBoxLabelValue) || !isTrimEmpty(translatedEqVasValueBoxLabelValue)) {
        pushTranslation({
          key: eqVasValueBoxKeyToSave,
          value: translatedEqVasValueBoxLabelValue,
          originalValue: originalLabelsRef.current.translatedEqVasValueBoxLabelValue,
          englishValue: eqVasValueBoxLabelValue,
          dataType: 'qsLabel',
        });
      }

      // Scale labels (current locale)
      scaleLabels.forEach((entry) => {
        const key = genScaleLabelKey(questionKey, entry.value);
        const original = originalLabelsRef.current.scaleLabels.find((o) => o.value === entry.value);
        if (
          !isTrimEmpty(entry.baseLabel) ||
          !isTrimEmpty(entry.translatedLabel) ||
          !isTrimEmpty(original?.translatedLabel || '')
        ) {
          pushTranslation({
            key,
            value: entry.translatedLabel,
            originalValue: original?.translatedLabel || '',
            englishValue: entry.baseLabel,
            dataType: 'qsLabel',
          });
        }
      });

      const thirdPartyLabelKey = genQuestionThirdPartyLabelKey(questionKey);
      pushTranslation({
        key: thirdPartyLabelKey,
        value: translatedThirdPartyLabel,
        originalValue: originalLabelsRef.current.translatedThirdPartyLabel,
        englishValue: englishThirdPartyLabel || '',
        dataType: 'qsLabel',
      });

      const thirdPartyDescInlineKey = genQuestionThirdPartyDescInlineKey(questionKey);
      pushTranslation({
        key: thirdPartyDescInlineKey,
        value: translatedThirdPartyDescInline,
        originalValue: originalLabelsRef.current.translatedThirdPartyDescInline,
        englishValue: englishThirdPartyDescInline || '',
        dataType: 'qsLabel',
      });

      const thirdPartyDescKey = genQuestionThirdPartyDescKey(questionKey);
      pushTranslation({
        key: thirdPartyDescKey,
        value: translatedThirdPartyDesc,
        originalValue: originalLabelsRef.current.translatedThirdPartyDesc,
        englishValue: englishThirdPartyDesc || '',
        dataType: 'qsLabel',
      });

      const thirdPartyExampleKey = genQuestionThirdPartyExampleKey(questionKey);
      pushTranslation({
        key: thirdPartyExampleKey,
        value: translatedThirdPartyExample,
        originalValue: originalLabelsRef.current.translatedThirdPartyExample,
        englishValue: englishThirdPartyExample || '',
        dataType: 'qsLabel',
      });

      const optionKeys = new Set<string>([
        ...Object.keys(originalLabelsRef.current.translatedOptionThirdPartyLabels),
        ...Object.keys(translatedOptionThirdPartyLabels),
        ...effectiveAnswerKeys,
      ]);

      optionKeys.forEach((optionKey) => {
        if (!optionKey) return;
        const optionMeta = (effectiveAnswerOptionsMap[optionKey] || {}) as AnswerOptionMetaLike;
        const englishOptionThirdPartyLabel = optionMeta.thirdPartyLabel || '';
        const hasTranslationValue = !isTrimEmpty(translatedOptionThirdPartyLabels[optionKey]);
        const hadOriginalValue = !isTrimEmpty(
          originalLabelsRef.current.translatedOptionThirdPartyLabels[optionKey],
        );
        const shouldTrackOption =
          optionMeta.useThirdPartyLabel === true || hasTranslationValue || hadOriginalValue;
        if (!shouldTrackOption) return;

        pushTranslation({
          key: genOptionThirdPartyLabelKey(questionKey, optionKey),
          value: translatedOptionThirdPartyLabels[optionKey] || '',
          originalValue:
            originalLabelsRef.current.translatedOptionThirdPartyLabels[optionKey] || '',
          englishValue: englishOptionThirdPartyLabel,
          dataType: 'option',
        });
      });
    }

    return translations;
  }, [
    surveyId,
    questionKey,
    baseLocale,
    questionLabel,
    answerLabels,
    currentLocale,
    translatedQuestionLabel,
    translatedAnswerLabels,
    translatedNestedQuestionLabels,
    descInlineValue,
    descValue,
    exampleValue,
    translatedDescInlineValue,
    translatedDescValue,
    translatedExampleValue,
    translatedThirdPartyLabel,
    translatedThirdPartyDescInline,
    translatedThirdPartyDesc,
    translatedThirdPartyExample,
    translatedOptionThirdPartyLabels,
    getDescInlineKeyForSave,
    getDescKeyForSave,
    getExampleKeyForSave,
    questionData?.answerKeys,
    questionData?.answerOptionsMap,
    questionData?.thirdPartyLabel,
    questionData?.thirdPartyDescInline,
    questionData?.thirdPartyDesc,
    questionData?.thirdPartyExample,
    questionWithLabels?.answerKeys,
    translationLockedMap,
    eqVasTopLabelValue,
    eqVasBottomLabelValue,
    eqVasValueBoxLabelValue,
    translatedEqVasTopLabelValue,
    translatedEqVasBottomLabelValue,
    translatedEqVasValueBoxLabelValue,
    scaleLabels,
  ]);

  // Save translations
  const saveTranslations = useCallback(async () => {
    const translationsToSave = buildTranslationsToSave();
    if (translationsToSave.length === 0) return;

    await translationMutation.mutateAsync({
      data: { translations: translationsToSave },
    });

    // 저장된 번역을 PaDataRepoState에 즉시 반영 (실시간 테스트 미리보기 즉시 업데이트)
    if (translationsToSave.length > 0) {
      setPaDataState((prev) => {
        const existingTranslations = prev.translations || [];
        const newTranslationsMap = new Map<string, (typeof prev.translations)[number]>();

        existingTranslations.forEach((tr) => {
          const uniqueKey = `${tr.key}-${tr.locale}`;
          newTranslationsMap.set(uniqueKey, tr);
        });

        translationsToSave.forEach((tr) => {
          const uniqueKey = `${tr.key}-${tr.locale}`;
          newTranslationsMap.set(uniqueKey, {
            id: null,
            key: tr.key || '',
            value: tr.value || null,
            locale: tr.locale || null,
            type: null,
            dataType: null,
          } as (typeof prev.translations)[number]);
        });

        return {
          ...prev,
          translations: Array.from(newTranslationsMap.values()),
        };
      });
    }

    // Invalidate translation-related queries to refresh labels
    const numericSurveyId = Number(surveyId);
    if (questionKey) {
      // Invalidate base locale labels query
      queryClient.invalidateQueries({
        queryKey: getQuestionControllerFindByKeyQueryKey({
          surveyId: numericSurveyId,
          questionKey,
          locale: baseLocale,
        }),
      });

      // Invalidate current locale labels query (if different from base locale)
      if (currentLocale !== baseLocale) {
        queryClient.invalidateQueries({
          queryKey: getQuestionControllerFindByKeyQueryKey({
            surveyId: numericSurveyId,
            questionKey,
            locale: currentLocale,
          }),
        });
      }
    }

    // Invalidate question list query to refresh labels in the list view (fire-and-forget)
    if (selectedTreeId) {
      queryClient.invalidateQueries({
        queryKey: getQuestionControllerFindByTreeIdQueryKey(selectedTreeId),
      });
    }

    const effectiveAnswerOptionsMapForOriginalRef = ((latestSaveDataRef.current
      ?.answerOptionsMap as Record<string, unknown> | undefined) ||
      (questionData?.answerOptionsMap as Record<string, unknown> | undefined) ||
      undefined) as Record<string, unknown> | undefined;

    // Update original labels ref after successful save
    originalLabelsRef.current = {
      questionLabel,
      answerLabels: { ...answerLabels },
      nestedQuestionLabels: buildNestedQuestionLabelMap({
        questionKey,
        answerOptionsMap: effectiveAnswerOptionsMapForOriginalRef,
      }),
      translatedQuestionLabel,
      translatedAnswerLabels: { ...translatedAnswerLabels },
      translatedNestedQuestionLabels: { ...translatedNestedQuestionLabels },
      // desc, descInline, example 값도 업데이트
      descInlineValue,
      descValue,
      exampleValue,
      translatedDescInlineValue,
      translatedDescValue,
      translatedExampleValue,
      translatedThirdPartyLabel,
      translatedThirdPartyDescInline,
      translatedThirdPartyDesc,
      translatedThirdPartyExample,
      translatedOptionThirdPartyLabels: { ...translatedOptionThirdPartyLabels },
      // EQ-VAS labels
      eqVasTopLabelValue,
      eqVasBottomLabelValue,
      eqVasValueBoxLabelValue,
      translatedEqVasTopLabelValue,
      translatedEqVasBottomLabelValue,
      translatedEqVasValueBoxLabelValue,
      // Scale labels
      scaleLabels: scaleLabels.map((l) => ({ ...l })),
    };
    originalTranslationLockedMapRef.current = { ...translationLockedMap };
  }, [
    buildTranslationsToSave,
    translationMutation,
    setPaDataState,
    surveyId,
    questionKey,
    baseLocale,
    currentLocale,
    selectedTreeId,
    queryClient,
    questionLabel,
    answerLabels,
    translatedQuestionLabel,
    translatedAnswerLabels,
    translatedNestedQuestionLabels,
    // desc, descInline, example 값들 추가
    descInlineValue,
    descValue,
    exampleValue,
    translatedDescInlineValue,
    translatedDescValue,
    translatedExampleValue,
    translatedThirdPartyLabel,
    translatedThirdPartyDescInline,
    translatedThirdPartyDesc,
    translatedThirdPartyExample,
    translatedOptionThirdPartyLabels,
    translationLockedMap,
    questionData?.answerOptionsMap,
    eqVasTopLabelValue,
    eqVasBottomLabelValue,
    eqVasValueBoxLabelValue,
    translatedEqVasTopLabelValue,
    translatedEqVasBottomLabelValue,
    translatedEqVasValueBoxLabelValue,
    scaleLabels,
  ]);

  // Save and close
  const handleSave = useCallback(
    async (data: Partial<QuestionData>) => {
      if (!questionId || !questionKey) return;

      try {
        // V1과 동일하게 descInline, desc, exampleKey는 번역 키로 저장
        const modifiedData = { ...data };

        // descInline: 값이 있으면 번역 키 저장, 없으면 null
        if (!isTrimEmpty(descInlineValue)) {
          modifiedData.descInline = getDescInlineKeyForSave(true);
        } else {
          modifiedData.descInline = null;
        }

        // desc: 값이 있으면 번역 키 저장, 없으면 null
        if (!isTrimEmpty(descValue)) {
          modifiedData.desc = getDescKeyForSave(true);
        } else {
          modifiedData.desc = null;
        }

        // exampleKey: 값이 있으면 번역 키 저장, 없으면 null
        if (!isTrimEmpty(exampleValue)) {
          modifiedData.exampleKey = getExampleKeyForSave(true);
        } else {
          modifiedData.exampleKey = null;
        }

        // Sync scale labels to typeOptions
        if (modifiedData.type === 'range' || questionData?.type === 'range') {
          modifiedData.typeOptions = {
            ...(modifiedData.typeOptions || questionData?.typeOptions || {}),
            scaleLabels: scaleLabels.map((entry) => entry.value),
          };
        }

        latestSaveDataRef.current = modifiedData;

        // 1. Save question data (번역 키 포함)
        const updateDto = convertToUpdateDto(modifiedData);
        await updateMutation.mutateAsync({ id: questionId, data: updateDto });

        // 2. Save translations (V1 saveLocales와 동일)
        await saveTranslations();

        // 3. 모달 닫기 + 쿼리 무효화
        toast.success('Question saved successfully');
        // onSaveClose가 있으면 URL 네비게이션 포함 닫기, 없으면 단순 닫기
        if (onSaveClose) {
          onSaveClose();
        } else {
          handleClose();
        }

        // 4. 쿼리 무효화 (리스트 + 개별 질문)
        queryClient.invalidateQueries({
          queryKey: getQuestionControllerFindByIdQueryKey(questionId),
        });
        // 질문 검색 리스트도 갱신 (Questions 페이지용)
        queryClient.invalidateQueries({
          queryKey: ['/questions/search'],
        });
      } catch (error) {
        // Error is handled in mutation onError
      } finally {
        latestSaveDataRef.current = null;
      }
    },
    [
      questionId,
      questionKey,
      questionData?.type,
      questionData?.typeOptions,
      convertToUpdateDto,
      updateMutation,
      saveTranslations,
      handleClose,
      onSaveClose,
      descInlineValue,
      descValue,
      exampleValue,
      getDescInlineKeyForSave,
      getDescKeyForSave,
      getExampleKeyForSave,
      queryClient,
      scaleLabels,
    ],
  );

  // Save and stay
  const handleSaveAndStay = useCallback(
    async (data: Partial<QuestionData>) => {
      if (!questionId || !questionKey) return;

      try {
        // V1과 동일하게 descInline, desc, exampleKey는 번역 키로 저장
        const modifiedData = { ...data };

        // descInline: 값이 있으면 번역 키 저장, 없으면 null
        if (!isTrimEmpty(descInlineValue)) {
          modifiedData.descInline = getDescInlineKeyForSave(true);
        } else {
          modifiedData.descInline = null;
        }

        // desc: 값이 있으면 번역 키 저장, 없으면 null
        if (!isTrimEmpty(descValue)) {
          modifiedData.desc = getDescKeyForSave(true);
        } else {
          modifiedData.desc = null;
        }

        // exampleKey: 값이 있으면 번역 키 저장, 없으면 null
        if (!isTrimEmpty(exampleValue)) {
          modifiedData.exampleKey = getExampleKeyForSave(true);
        } else {
          modifiedData.exampleKey = null;
        }

        // Sync scale labels to typeOptions
        if (modifiedData.type === 'range' || questionData?.type === 'range') {
          modifiedData.typeOptions = {
            ...(modifiedData.typeOptions || questionData?.typeOptions || {}),
            scaleLabels: scaleLabels.map((entry) => entry.value),
          };
        }

        latestSaveDataRef.current = modifiedData;

        // 1. Save question data (번역 키 포함)
        const updateDto = convertToUpdateDto(modifiedData);
        await updateMutation.mutateAsync({ id: questionId, data: updateDto });

        // 2. Save translations (V1 saveLocales와 동일)
        await saveTranslations();

        toast.success('Question saved successfully');

        // 3. 쿼리 무효화 후 refetch (fire-and-forget)
        queryClient.invalidateQueries({
          queryKey: getQuestionControllerFindByIdQueryKey(questionId),
        });
        queryClient.invalidateQueries({
          queryKey: ['/questions/search'],
        });
        refetch();
      } catch (error) {
        // Error is handled in mutation onError
      } finally {
        latestSaveDataRef.current = null;
      }
    },
    [
      questionId,
      questionKey,
      questionData?.type,
      questionData?.typeOptions,
      convertToUpdateDto,
      updateMutation,
      saveTranslations,
      refetch,
      descInlineValue,
      descValue,
      exampleValue,
      getDescInlineKeyForSave,
      getDescKeyForSave,
      getExampleKeyForSave,
      queryClient,
      scaleLabels,
    ],
  );

  return {
    // Modal state
    isOpen,
    questionKey,
    questionId,

    // Data
    questionData,
    isLoading,

    // Labels (English)
    questionLabel,
    answerLabels,
    currentLocaleAnswerLabels,

    // Translated labels (current locale)
    translatedQuestionLabel,
    translatedAnswerLabels,
    translatedNestedQuestionLabels,

    // Translation lock
    translationLockedMap,
    handleTranslationLockChange,

    // Desc, DescInline, Example 번역 값 (English)
    descInlineValue,
    descValue,
    exampleValue,

    // Desc, DescInline, Example 번역 값 (current locale)
    translatedDescInlineValue,
    translatedDescValue,
    translatedExampleValue,
    translatedThirdPartyLabel,
    translatedThirdPartyDescInline,
    translatedThirdPartyDesc,
    translatedThirdPartyExample,
    translatedOptionThirdPartyLabels,

    // Current locale
    currentLocale,

    // Handlers
    handleOpen,
    handleClose,
    handleLabelChange,
    handleAnswerLabelChange,
    handleTranslatedLabelChange,
    handleTranslatedAnswerLabelChange,
    handleTranslatedNestedQuestionLabelChange,
    handleLocaleChange,
    handleSave,
    handleSaveAndStay,

    // Desc, DescInline, Example 핸들러
    handleDescInlineChange,
    handleDescChange,
    handleExampleChange,
    handleTranslatedDescInlineChange,
    handleTranslatedDescChange,
    handleTranslatedExampleChange,
    handleTranslatedThirdPartyLabelChange,
    handleTranslatedThirdPartyDescInlineChange,
    handleTranslatedThirdPartyDescChange,
    handleTranslatedThirdPartyExampleChange,
    handleTranslatedOptionThirdPartyLabelChange,

    // EQ-VAS labels
    eqVasTopLabelValue,
    eqVasBottomLabelValue,
    eqVasValueBoxLabelValue,
    translatedEqVasTopLabelValue,
    translatedEqVasBottomLabelValue,
    translatedEqVasValueBoxLabelValue,
    handleEqVasTopLabelChange,
    handleEqVasBottomLabelChange,
    handleEqVasValueBoxLabelChange,
    handleTranslatedEqVasTopLabelChange,
    handleTranslatedEqVasBottomLabelChange,
    handleTranslatedEqVasValueBoxLabelChange,

    // Scale labels
    scaleLabels,
    showScaleLabels,
    setShowScaleLabels,
    handleAddScaleLabel,
    handleRemoveScaleLabel,
    handleScaleLabelValueChange,
    handleScaleLabelTextChange,
    handleScaleLabelTranslatedTextChange,

    // Image upload
    imageUrl,
    isUploading,
    handleImageUpload,
    handleImageRemove,

    // States
    isSaving: updateMutation.isPending || translationMutation.isPending,
  };
}
